import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  errorToast,
  successToast,
  handleErrors,
  safeHandleGenericError,
} from "@/app/_module/services/helper";
import type {
  CreateReminderRequest,
  UpdateReminderRequest,
  UpdateReminderStatusRequest, // Import from types file
  ReminderUI,
  Reminder,
} from "@/app/_module/types/reminders";
import {
  convertApiToUI,
  convertUIToApi,
} from "@/app/_module/types/reminders";

import { remindersService } from "../services/reminders-service";

// Remove the local interface definition since we're importing it
// interface UpdateReminderStatusRequest {
//   remStat?: "pending" | "completed" | "cancelled" | "overdue";
//   remPriorStat?: "urgent" | "high" | "medium" | "low";
// }

// Query keys
// Note: Overdue status is now managed by the backend after 10 minutes of being NOTIFIED
// The frontend no longer auto-detects overdue status client-side

export const reminderKeys = {
  all: ["reminders"] as const,
  lists: () => [...reminderKeys.all, "list"] as const,
  list: (filters: { entityType?: string }) =>
    [...reminderKeys.lists(), filters] as const,
  infinite: (filters: { entityType?: string }) =>
    [...reminderKeys.lists(), "infinite", filters] as const,
  details: () => [...reminderKeys.all, "detail"] as const,
  detail: (id: string) => [...reminderKeys.details(), id] as const,
};

// ✅ Get all reminders without pagination
export const useReminders = (params?: {
  entityType?: string;
  refreshInterval?: number; // Auto-refresh interval to pick up backend status changes
}) => {
  const { refreshInterval = 60000, ...queryParams } = params || {};

  return useQuery({
    queryKey: reminderKeys.list(queryParams),
    queryFn: async () => {
      let allItems: unknown[] = [];
      let currentPage = 1;
      let hasMorePages = true;
      let meta = {
        page: 1,
        nextPage: null as number | null,
        prevPage: null as number | null,
        offset: 0,
        totalItems: 0,
        totalPages: 1,
        itemCount: 0
      };

      // Fetch all pages (your existing logic)
      while (hasMorePages && currentPage <= 10) {
        const response = await remindersService.getReminders({
          ...queryParams,
          page: currentPage,
          limit: 100,
          sortBy: 'createdAt',
          sortOrder: 'desc',
        });

        allItems = [...allItems, ...response.items];

        if (currentPage === 1) {
          meta = { ...response.meta };
        }

        hasMorePages = response.meta.nextPage !== null;
        currentPage++;
      }

      // Deduplicate items
      const uniqueItems = allItems.filter((item: unknown, index: number, array: unknown[]) =>
        array.findIndex((i: unknown) => (i as Reminder).id === (item as Reminder).id) === index
      );

      // Convert to UI format - status (including overdue) is now managed by the backend
      const uiItems = uniqueItems.map((item: unknown) =>
        convertApiToUI(item as Reminder) as unknown as ReminderUI
      );

      return {
        meta: {
          ...meta,
          totalItems: uiItems.length,
          itemCount: uiItems.length,
        },
        items: uiItems,
      };
    },
    staleTime: 0, // Always consider data stale to refetch on mount for instant refresh
    refetchOnMount: true, // Ensure fresh data on mount
    refetchOnWindowFocus: true, // Refetch when returning to tab
    // Auto-refresh to pick up backend status changes (notified -> overdue)
    refetchInterval: refreshInterval,
    refetchIntervalInBackground: false, // Only poll when tab is active — saves server load
  });
};
// ✅ Get single reminder
export const useReminder = (reminderId: string) => {
  return useQuery({
    queryKey: reminderKeys.detail(reminderId),
    queryFn: async () => {
      const response = await remindersService.getReminderById(reminderId);
      return convertApiToUI(response);
    },
    enabled: !!reminderId,
    staleTime: 0, // Always consider data stale to refetch on mount for instant refresh
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });
};

// ✅ Create reminder
export const useCreateReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reminderData: Partial<ReminderUI>) => {
      // Step 1: Create the reminder
      // DON'T use convertUIToApi - it strips out associations!
      // The NewReminderDialog already formats the data correctly with associations
      const createdReminder = await remindersService.createReminder(reminderData as CreateReminderRequest);
      if (!createdReminder || typeof createdReminder !== "object" || !createdReminder.date) {
        throw new Error("Invalid API response: missing 'date'");
      }

      // Priority is already included in the create request, no need for separate update
      return convertApiToUI(createdReminder);
    },
    onSuccess: async (newReminder) => {
      // Invalidate all reminder queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: reminderKeys.all });
      
      // Also add the new reminder to all relevant caches
      queryClient.setQueriesData(
        { queryKey: reminderKeys.all },
        (oldData: unknown) => {
          if (oldData && typeof oldData === 'object' && 'items' in oldData) {
            const data = oldData as { items: ReminderUI[] };
            // Check if reminder already exists to avoid duplicates
            const exists = data.items.some(item => item.id === newReminder.id);
            if (!exists) {
              return {
                ...data,
                items: [newReminder, ...data.items] // Add to beginning for newest first
              };
            }
          }
          return oldData;
        }
      );
    },
    onError: (error: unknown) => {
      // Don't show toast here - let the component handle it

    },
  });
};

export const useUpdateReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reminderId,
      reminderData,
      statusOnly = false,
    }: {
      reminderId: string;
      reminderData: Partial<ReminderUI>;
      statusOnly?: boolean;
    }) => {

      const hasStatusChange = reminderData.status !== undefined;
      const hasPriorityChange = reminderData.priority !== undefined;

      // Get all fields except status, priority, and id
      const otherFields = Object.keys(reminderData).filter(key =>
        key !== 'status' && key !== 'priority' && key !== 'id' &&
        reminderData[key as keyof typeof reminderData] !== undefined
      );

      // Quick status/priority-only updates from card (statusOnly = true)
      if (statusOnly && (hasStatusChange || hasPriorityChange) && otherFields.length === 0) {
        const statusData: UpdateReminderStatusRequest = {};

        if (hasStatusChange) statusData.remStat = reminderData.status;
        if (hasPriorityChange) statusData.remPriorStat = reminderData.priority;

        const response = await remindersService.updateReminderStatus(reminderId, statusData);
        return convertApiToUI(response);
      }

      // Dialog updates (statusOnly = false) - Use two-phase approach
      if (!statusOnly) {
        let latestResponse;

        // Phase 1: Update status/priority first if they changed
        if (hasStatusChange || hasPriorityChange) {
          const statusData: UpdateReminderStatusRequest = {};
          if (hasStatusChange) statusData.remStat = reminderData.status;
          if (hasPriorityChange) statusData.remPriorStat = reminderData.priority;

          try {
            latestResponse = await remindersService.updateReminderStatus(reminderId, statusData);
          } catch (error) {
            throw error; // Don't continue if status update fails
          }
        }

        // Phase 2: Update other fields if there are any
        if (otherFields.length > 0) {

          // Create a copy without status/priority since they're already updated
          const otherFieldsData = { ...reminderData };
          delete otherFieldsData.status;
          delete otherFieldsData.priority;

          // DON'T use convertUIToApi for associations - it rebuilds them from scratch
          // Instead, pass associations through directly like we do for create
          // The NewReminderDialog already formats associations correctly
          const hasDirectAssociations = Array.isArray(reminderData.associations) && reminderData.associations.length > 0;

          let apiData: UpdateReminderRequest;
          if (hasDirectAssociations) {
            // Pass through the pre-formatted data directly, similar to create flow
            apiData = otherFieldsData as UpdateReminderRequest;
          } else {
            // Fall back to convertUIToApi for backwards compatibility
            apiData = convertUIToApi(otherFieldsData) as UpdateReminderRequest;
          }

          try {
            latestResponse = await remindersService.updateReminder(reminderId, apiData);
          } catch (error) {
            // If we have a response from phase 1, use it
            if (!latestResponse) {
              throw error;
            }
          }
        }

        // If we don't have any response, fetch the current reminder
        if (!latestResponse) {
          latestResponse = await remindersService.getReminderById(reminderId);
        }

        return convertApiToUI(latestResponse);
      }

      // Fallback (shouldn't reach here)
      // Use same logic as above - pass through directly if associations exist
      const hasFallbackAssociations = Array.isArray(reminderData.associations) && reminderData.associations.length > 0;
      const apiData = hasFallbackAssociations
        ? (reminderData as UpdateReminderRequest)
        : (convertUIToApi(reminderData) as UpdateReminderRequest);
      const response = await remindersService.updateReminder(reminderId, apiData);
      return convertApiToUI(response);
    },
    onSuccess: async (updatedReminder) => {
      // Don't show toast here - let the component handle it

      // Update the specific reminder in cache immediately
      queryClient.setQueryData(
        reminderKeys.detail(updatedReminder.id),
        updatedReminder
      );

      // Instead of invalidating all queries, update the list cache directly
      queryClient.setQueriesData(
        { queryKey: reminderKeys.all },
        (oldData: unknown) => {
          if (oldData && typeof oldData === 'object' && 'items' in oldData) {
            const data = oldData as { items: ReminderUI[] };
            return {
              ...data,
              items: data.items.map(item =>
                item.id === updatedReminder.id ? updatedReminder : item
              )
            };
          }
          return oldData;
        }
      );

      // Invalidate queries to ensure fresh data
      await queryClient.invalidateQueries({ queryKey: reminderKeys.all });
    },
    onError: (error: unknown) => {
      // Don't show toast here - let the component handle it

    },
  });
};

export const useUpdateReminderStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      reminderId,
      statusData,
    }: {
      reminderId: string;
      statusData: UpdateReminderStatusRequest;
    }) => {
      const response = await remindersService.updateReminderStatus(reminderId, statusData);
      return convertApiToUI(response);
    },
    onSuccess: async (updatedReminder) => {
      successToast("Reminder status updated successfully!");
      await queryClient.invalidateQueries({ queryKey: reminderKeys.all });

      // Also update the specific reminder in cache if it exists
      queryClient.setQueryData(
        reminderKeys.detail(updatedReminder.id),
        updatedReminder
      );
    },
    onError: (error: unknown) => {
      errorToast(safeHandleGenericError(error, handleErrors));
    },
  });
};

// ✅ Delete reminder
export const useDeleteReminder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (reminderId: string) =>
      remindersService.deleteReminder(reminderId),
    onSuccess: async (_, deletedReminderId) => {
      successToast("Reminder deleted successfully!");

      // Remove the reminder from cache directly
      queryClient.removeQueries({
        queryKey: reminderKeys.detail(deletedReminderId)
      });

      // Update list cache by removing the deleted item
      queryClient.setQueriesData(
        { queryKey: reminderKeys.all },
        (oldData: unknown) => {
          if (oldData && typeof oldData === 'object' && 'items' in oldData) {
            const data = oldData as { items: ReminderUI[] };
            return {
              ...data,
              items: data.items.filter(item => item.id !== deletedReminderId)
            };
          }
          return oldData;
        }
      );

      // Only invalidate as fallback if direct update fails
      // await Promise.all([
      //   queryClient.invalidateQueries({ queryKey: reminderKeys.all })
      // ]);
    },
    onError: (error: unknown) => {
      errorToast(safeHandleGenericError(error, handleErrors));
    },
  });
};